<script setup lang="ts">
import {
  cellIndicatorsMapping,
  drawerVisible, indicatorsVisible, getIndicatorInfo, judgmentOptions, kdm, percentagesVisible,
  selectedCell, isGlobalReloading, editMode, discussionState, getCommentIcon, loadKdm,
  computeCellWinner,
} from "@/store/kdm";
import {computed, watch} from "vue";
import {useFetch} from "@/composables/useFetch";
import {SliderUpdate} from "#/KdmNode";
import IndicatorsView from "@/kdm/cell/IndicatorsView.vue";
import SummaryView from "@/kdm/cell/SummaryView.vue";
import SimpleCellView from "@/kdm/cell/SimpleCellView.vue";

const ofetch = useFetch()

const props = defineProps<{
  rid: string
  cid: string
  tid: string
}>()

// ============ COMPUTED - Derived from field_matrix_data (single source of truth) ============

const cellId = computed(() => `${props.tid}-${props.rid}-${props.cid}`)

// Parse matrix data once
const matrixData = computed(() => {
  if (!kdm.value?.field_matrix_data) return {}
  try {
    return typeof kdm.value.field_matrix_data === 'string'
      ? JSON.parse(kdm.value.field_matrix_data)
      : kdm.value.field_matrix_data
  } catch (e) {
    return {}
  }
})

// Cell data derived directly from matrix data
const cellData = computed(() => {
  const raw = matrixData.value[cellId.value]
  const data = (!raw || Array.isArray(raw)) ? {} : raw
  const entries = Object.entries(data).filter(([_, val]) =>
    val && typeof val === 'object' && ('sliderValue' in val || 'judgmentId' in val)
  )
  return new Map(entries as [string, { sliderValue: number, judgmentId: string }][])
})

// Cell indicators mapping for display
const cellIndicators = computed(() => {
  const entries = Array.from(cellData.value.keys()).map(id => [id, getIndicatorInfo(id)])
  entries.sort((a, b) => {
    const titleA = (a[1] as any).title || '';
    const titleB = (b[1] as any).title || '';
    return titleA.localeCompare(titleB);
  })
  return new Map(entries as any)
})

// Keep cellIndicatorsMapping in sync (used by child components)
watch(cellIndicators, (newMapping) => {
  cellIndicatorsMapping[cellId.value] = newMapping
}, { immediate: true })

const active = computed(() => selectedCell.value === cellId.value)

// Use shared algorithm for cell winner calculation
const cellWinner = computed(() => {
  const rawCellData = matrixData.value[cellId.value]
  return computeCellWinner(rawCellData, kdm.value?.kdm_type)
})

const cellTotalsMax = computed(() => cellWinner.value?.totalWeight || 0)

const cellTotalsColor = computed(() =>
  cellWinner.value?.judgmentId
    ? `var(--judgment-option-${cellWinner.value.judgmentId})`
    : 'transparent'
)

const activeComponent = computed(() => {
  if (kdm.value?.kdm_type === 1) {
    return editMode.value ? SimpleCellView : SummaryView;
  }
  return indicatorsVisible.value ? IndicatorsView : SummaryView;
})

const isLocked = computed(() => {
  if (!kdm.value?.current_user) return false;
  const cu = kdm.value.current_user;
  // Admins, owners, coordinators, and editors can edit any cell
  if (cu.is_admin || cu.is_owner || cu.is_coordinator || cu.is_editor) return false;

  const assignments = matrixData.value.assignments || {};
  const assignedIds = assignments[props.tid] || assignments[String(props.tid)] || [];
  const allowedUserIds = assignedIds.map((id: any) => String(id));
  const currentUserId = String(cu.id);

  return !allowedUserIds.includes(currentUserId);
})

const commentIcon = computed(() => getCommentIcon(cellId.value))

const maxArgumentsReached = computed(() => {
  const max = kdm.value?.max_arguments_per_cell || 0;
  if (max === 0) return false;
  return cellData.value.size >= max;
})

// ============ ACTIONS ============

function openDiscussion() {
  discussionState.coords = cellId.value;
  discussionState.nodeId = kdm.value?.nid || '';
  discussionState.canWrite = !isLocked.value;
  discussionState.visible = true;
}

function openDrawerAndSelectCell(): void {
  if (isLocked.value) return;
  selectedCell.value = cellId.value;
  drawerVisible.value = true;
}

// Save cell data to server
async function saveToServer(newCellData: Record<string, any>) {
  if (!kdm.value?.nid) return

  const requestBody: Record<string, any> = {}
  requestBody[cellId.value] = newCellData

  try {
    const response = await ofetch<any>(`/epbapi/kdm/${kdm.value.nid}/`, {
      method: 'PATCH',
      body: requestBody,
    })
    if (response.data) {
      kdm.value.field_matrix_data = response.data
    }
  } catch (err: any) {
    console.error("Failed to patch KDM cell:", err.data);
    alert(`Error saving changes: ${err.data?.message || 'You may not have the required permissions.'}`);
    loadKdm(kdm.value.nid as string);
  }
}

// Called when slider/select changes in child component
function updateCellData(data: SliderUpdate): void {
  if (kdm.value?.kdm_type === 1) {
    // Type 1: Simple cell, save directly
    saveToServer(data)
  } else {
    // Type 2/3: Update indicator in cell
    const currentData = Object.fromEntries(cellData.value)
    currentData[data.indicatorId] = { sliderValue: data.sliderValue, judgmentId: data.judgmentId }
    saveToServer(currentData)
  }
}

// Called when indicator is removed
function removeFromCellData(indicatorId: string): void {
  const currentData = Object.fromEntries(cellData.value)
  delete currentData[indicatorId]
  saveToServer(currentData)
}
</script>

<template>
  <td
    class="kdm__cell"
    :class="{ 'active-cell': active, 'locked-cell': isLocked && editMode }"
  >
    <div
      class="kdm__cell-flex-container"
      style="display: flex !important; flex-direction: column !important; min-height: 60px; justify-content: space-between !important;"
    >
      <div v-if="isLocked && editMode" class="text-center text-muted mb-1">
        <i class="bi bi-lock-fill" title="Locked by assignment"></i>
      </div>
      <div class="kdm__cell-content" style="flex-grow: 1 !important;">
        <KeepAlive>
          <component :is="activeComponent"
                     :cell-id="cellId"
                     :cell-indicators-mapping="cellIndicatorsMapping"
                     :cell-totals-color="cellTotalsColor"
                     :cell-totals-max="cellTotalsMax"
                     :percentages-visible="percentagesVisible && kdm?.kdm_type !== 1"
                     :is-locked="isLocked"
                     :comment-icon="!editMode ? commentIcon : undefined"
                     @update="updateCellData"
                     @delete="removeFromCellData"
                     @open-discussion="openDiscussion"
          />
        </KeepAlive>
      </div>

      <div
        v-if="kdm?.kdm_type !== 1 && editMode"
        class="kdm__cell-footer d-flex justify-content-between align-items-center mt-1"
        style="margin-top: 4px;"
      >
        <button
          type="button"
          class="btn btn-sm btn-link text-decoration-none p-0"
          @click.stop="openDiscussion"
          title="Discuss this cell"
        >
          <i class="bi" :class="[commentIcon.icon, commentIcon.class]"></i>
        </button>

        <button
          v-if="indicatorsVisible && !isLocked && !maxArgumentsReached"
          type="button"
          class="btn btn-sm btn-success add-node-btn"
          style="background-color: #198754; border-color: #198754; color: white; font-weight: bold; padding: 0.25rem 0.5rem; opacity: 0.8;"
          @click.stop="openDrawerAndSelectCell"
          title="Add indicators"
        >
          <span>+</span>
        </button>
        <small v-if="maxArgumentsReached" class="text-muted">Max arguments reached</small>
      </div>
    </div>
  </td>
</template>

<style scoped>
.kdm__cell {
  padding: 8px;
  vertical-align: top;
  width: var(--kdm-column-width, 150px);
  min-width: var(--kdm-column-width, 150px);
  max-width: var(--kdm-column-width, 150px);
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.kdm__cell.active-cell {
  outline: 2px solid #0d6efd;
  background-color: #e7f1ff;
}

.kdm__cell.locked-cell {
  background-color: #f8f9fa;
  cursor: not-allowed;
}

.add-node-btn:hover {
  opacity: 1 !important;
  background-color: #157347 !important;
}
</style>
